<template>
  <FriedModal class="share-room-modal" :closable="false">
    <template #header>
      <FriedModalHeader :header="t('dr_share_room')" />
    </template>
    <div :class="['share-room-modal-container', { 'has-universal-link': showUniversalLink }]">
      <FriedParagraph v-if="currentStep === InviteStep.Invite">
        {{ t('dr_enter_the_email_address_to_share_room_link.') }}
      </FriedParagraph>
      <UniversalLink v-if="showUniversalLink" class="universal-link" :url="universalLink" />
      <div class="invite-participant-container">
        <div v-show="currentStep === InviteStep.Invite">
          <InviteParticipant
            v-for="(participant, index) in participantsToInvite"
            :key="participant.id"
            :participant="participant"
            :current-participants="currentParticipants"
            :participants-to-invite="participantsToInvite"
            :is-last="index === participantsToInvite.length - 1"
            :has-started-editing="hasStartedEditing"
            @update="updateParticipant($event, participant.id)"
            @remove="removeParticipant(participant.id)"
          />
          <TransitionExpand>
            <div
              v-if="showAddMoreParticipantsButton"
              ref="addMoreParticipantsContainer"
              class="button-container"
            >
              <FriedRoundedButton
                data-external="add-another-participant-button"
                @click="addParticipant"
              >
                <template #icon>
                  <FriedIcon icon="plus-large" />
                </template>
                {{ t('dr_invite_and_share_add_new') }}</FriedRoundedButton
              >
            </div>
          </TransitionExpand>
        </div>
        <div v-show="currentStep === InviteStep.Share" class="share-step">
          <FriedTextarea
            v-model="notificationMessage"
            data-external="invite-message-input"
            class="invite-input"
            :rows="8"
          />
        </div>
      </div>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <template v-if="currentStep === InviteStep.Share" #left>
          <FriedButton
            data-external="share-room-modal-back-button"
            :button-type="ButtonType.Tertiary"
            @click="currentStep = InviteStep.Invite"
          >
            <template #leftIcon>
              <FriedIcon icon="arrow-left" />
            </template>
            {{ t('back') }}
          </FriedButton>
        </template>
        <FriedButton
          data-external="share-room-modal-cancel-button"
          :button-type="ButtonType.Secondary"
          @click="$emit('close')"
        >
          {{ t('cancel') }}
        </FriedButton>
        <FriedButton
          v-if="currentStep === InviteStep.Invite"
          :disabled="inviteDisabled"
          data-external="share-room-modal-next-step"
          @click="currentStep = InviteStep.Share"
        >
          {{ t('next') }}
        </FriedButton>
        <FriedButton
          v-else-if="currentStep === InviteStep.Share"
          data-external="share-room-modal-share-button"
          :loading="loading"
          :disabled="!notificationMessage"
          @click="invite"
        >
          {{ t('dsr_button_send_invitation_message') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue';
import { computed, defineComponent, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Participant } from '@getaccept/dsr-shared-new';
import { UniversalLink } from '@getaccept/dsr-shared-new';
import { ButtonType } from '@getaccept/fried-tofu';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import { nanoid } from 'nanoid';
import { storeToRefs } from 'pinia';
import { Feature } from '@getaccept/lib-shared-new/src/feature-restriction/types/feature';
import { useFeatureStore } from '@getaccept/lib-shared-new/src/feature-restriction/store/feature.store';
import { DsrService } from '../../api/dsr/service/dsr.service';
import { ShareHelper } from '../helpers/share.helper';
import type { ParticipantInvite } from '../types/participant-invite';
import InviteParticipant from './InviteParticipant.vue';

enum InviteStep {
  Invite,
  Share,
}

export default defineComponent({
  components: {
    UniversalLink,
    InviteParticipant,
  },
  props: {
    universalLink: {
      type: String,
      default: '',
    },
    currentParticipants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    roomName: {
      type: String,
      default: '',
    },
    senderName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const emptyParticipant = {
      id: nanoid(),
      email: '',
      firstName: '',
      lastName: '',
      companyName: '',
      title: '',
    };

    const currentStep = ref(InviteStep.Invite);
    const participantsToInvite = ref<ParticipantInvite[]>([structuredClone(emptyParticipant)]);
    const loading = ref(false);
    const hasStartedEditing = ref(false);
    const addMoreParticipantsContainer = ref<HTMLDivElement | null>(null);

    const { features } = storeToRefs(useFeatureStore());

    const hasParticipantLimit = computed(
      () =>
        features.value[Feature.RecipientLimit].restricted ||
        !!features.value[Feature.RecipientLimit].threshold
    );

    const reachedMaxParticipantsOnLegacyPlan = computed(
      () =>
        features.value[Feature.RecipientLimit].restricted &&
        participantsToInvite.value.length + props.currentParticipants.length >= 5
    );

    const reachedMaxParticipants = computed(
      () =>
        reachedMaxParticipantsOnLegacyPlan.value ||
        participantsToInvite.value.length + props.currentParticipants.length >=
          features.value[Feature.RecipientLimit].threshold
    );

    const someParticipantsHaveEmail = computed(() =>
      participantsToInvite.value.some(participant => participant.email !== '')
    );

    const showAddMoreParticipantsButton = computed(() => {
      if (hasStartedEditing.value || someParticipantsHaveEmail.value) {
        return !hasParticipantLimit.value || !reachedMaxParticipants.value;
      }
      return false;
    });

    watch(
      participantsToInvite,
      () => {
        if (!hasStartedEditing.value) {
          hasStartedEditing.value = true;
        }

        if (
          participantsToInvite.value.length === 1 &&
          participantsToInvite.value[0].email === '' &&
          hasStartedEditing.value
        ) {
          hasStartedEditing.value = false;
        }
      },
      { deep: true }
    );

    const inviteDisabled = computed(
      () =>
        (participantsToInvite.value.length === 1 && participantsToInvite.value[0].email === '') ||
        participantsToInvite.value.some(
          participant =>
            !participant.email ||
            !!ShareHelper.validateEmail(
              participant.email,
              props.currentParticipants,
              participantsToInvite.value
            )
        )
    );

    const defaultInviteMessage = computed(() =>
      t('dr_invite_and_share_default_message_v2', {
        dealroomName: props.roomName,
        senderFirstName: props.senderName,
      })
    );

    const notificationMessage = ref(defaultInviteMessage.value);

    const showUniversalLink = computed(
      () => !!props.universalLink && currentStep.value === InviteStep.Invite
    );

    const scrollToAddMoreParticipants = () => {
      if (!addMoreParticipantsContainer.value) return;
      addMoreParticipantsContainer.value?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    const addParticipant = async () => {
      participantsToInvite.value.push({ ...emptyParticipant, id: nanoid() });
      await nextTick();
      scrollToAddMoreParticipants();
    };

    const invite = () => {
      if (inviteDisabled.value) {
        return;
      }
      loading.value = true;
      const filteredParticipants = participantsToInvite.value.filter(
        participant => !!participant.email
      );
      sendInvitations(filteredParticipants);
    };

    watch(currentStep, (newStep, oldStep) => {
      if (
        newStep === InviteStep.Invite &&
        oldStep === InviteStep.Share &&
        !notificationMessage.value
      ) {
        notificationMessage.value = defaultInviteMessage.value;
      }
    });

    const sendInvitations = async (participants: ParticipantInvite[]) => {
      let invitedParticipants = [...participants];
      const results: Ref<{ status: string }[]> = ref([]);

      try {
        results.value = await Promise.allSettled(
          participants.map((participant: ParticipantInvite) =>
            DsrService.inviteParticipant(participant, notificationMessage.value)
          )
        );
      } catch (error) {
        console.error(error);
      }

      results.value.forEach((result, index) => {
        if (result.status === 'rejected') {
          const errorParticipant = participants[index];
          useToast().toast.danger(
            t('dr_invite_and_share_error_message', { email: errorParticipant.email })
          );
          invitedParticipants = invitedParticipants.filter(
            participant => participant.email !== errorParticipant.email
          );
        }
      });

      emit('invite-success', {
        participants: invitedParticipants,
        notificationMessage: notificationMessage.value,
      });
      loading.value = false;
    };

    const updateParticipant = (participant: ParticipantInvite, id: string) => {
      participantsToInvite.value = participantsToInvite.value.map(p =>
        p.id === id ? participant : p
      );
    };

    const removeParticipant = (id: string) => {
      if (participantsToInvite.value.length === 1) {
        participantsToInvite.value = [{ ...emptyParticipant, id: nanoid() }];
        hasStartedEditing.value = false;
        return;
      }

      participantsToInvite.value = participantsToInvite.value.filter(
        participant => participant.id !== id
      );
    };

    return {
      addParticipant,
      invite,
      sendInvitations,
      participantsToInvite,
      inviteDisabled,
      showAddMoreParticipantsButton,
      loading,
      notificationMessage,
      t,
      ButtonType,
      currentStep,
      InviteStep,
      defaultInviteMessage,
      showUniversalLink,
      updateParticipant,
      removeParticipant,
      hasStartedEditing,
      addMoreParticipantsContainer,
    };
  },
});
</script>
<style lang="scss" scoped>
.share-room-modal {
  display: flex;
  align-items: center;

  .share-room-modal-container {
    height: 320px;

    &.has-universal-link {
      height: 380px;
    }
  }

  .universal-link {
    padding-bottom: var(--spacing-100);
    background: var(--white);
  }

  .invite-participant-container {
    width: 100%;
    padding: var(--spacing-100) 0;
    max-height: 300px;
    overflow-y: auto;

    .invite-input,
    .button-container {
      width: inherit;
    }

    .button {
      margin-top: var(--spacing-100);
    }

    .share-step {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-50);
    }
  }
}
</style>
